import React, { useState, useEffect, useCallback } from 'react';
import { UploadQueueManager } from '../utils/UploadQueueManager';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  CheckCircle as ValidatedIcon,
  CheckCircle,
  Warning as ReviewIcon,
  HourglassEmpty as ProcessingIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
// import { format } from 'date-fns'; // Commented out - will use basic formatting

interface UploadRecord {
  report_id: string;
  filename: string;
  uploaded_at: string;
  status: string;
  confidence: number;
  s3_key: string;
  file_type: string;
  file_size: number;
  user_validated: boolean;
  validation_timestamp: string;
}

interface UserHistoryTableProps {
  userEmail: string;
  apiEndpoint: string;
  onViewReport: (reportId: string) => void;
  refreshTrigger?: number;
  uploadManager?: UploadQueueManager;
}

type OrderDirection = 'asc' | 'desc';
type OrderBy = 'uploaded_at' | 'filename' | 'status' | 'confidence';

const UserHistoryTable: React.FC<UserHistoryTableProps> = ({
  userEmail,
  apiEndpoint,
  onViewReport,
  refreshTrigger,
  uploadManager,
}) => {
  const { getIdTokenClaims } = useAuth0();
  const [uploads, setUploads] = useState<UploadRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [orderBy, setOrderBy] = useState<OrderBy>('uploaded_at');
  const [orderDirection, setOrderDirection] = useState<OrderDirection>('desc');
  const [hasMore, setHasMore] = useState(false);
  const [lastKey, setLastKey] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const fetchUserUploads = useCallback(async (pageKey?: string) => {
    setLoading(true);
    setError(null);

    try {
      // Get Auth0 token
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      const queryParams = new URLSearchParams({
        limit: rowsPerPage.toString(),
      });

      if (pageKey) {
        queryParams.append('lastKey', pageKey);
      }

      const response = await fetch(`${apiEndpoint}/user-uploads?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch upload history');
      }

      const data = await response.json();

      if (pageKey) {
        // Append to existing uploads for pagination
        setUploads(prev => {
          const newUploads = [...prev, ...data.uploads];
          return newUploads;
        });
      } else {
        // Replace uploads for initial load or refresh
        setUploads(data.uploads);
      }

      setHasMore(data.hasMore);
      setLastKey(data.lastKey);
      setTotalCount(prev => prev + data.totalReturned);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [userEmail, apiEndpoint, rowsPerPage]);

  useEffect(() => {
    fetchUserUploads();
  }, [fetchUserUploads, refreshTrigger]);

  // Separate effect to handle EML completion checks when uploads change
  useEffect(() => {
    if (uploadManager && uploads.length > 0) {
      uploadManager.checkEmlAttachmentCompletion(uploads);
    }
  }, [uploads, uploadManager]);

  const handleSort = (property: OrderBy) => {
    const isAsc = orderBy === property && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedUploads = React.useMemo(() => {
    return [...uploads].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue < bValue) {
        return orderDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return orderDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [uploads, orderBy, orderDirection]);

  const paginatedUploads = sortedUploads.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);

    // Load more if needed
    if (newPage * rowsPerPage >= uploads.length - 5 && hasMore && !loading) {
      fetchUserUploads(lastKey || undefined);
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusIcon = (status: string, confidence: number) => {
    if (status === 'validated') {
      return <ValidatedIcon color="success" />;
    } else if (status === 'completed' && confidence >= 0.7) {
      return <CheckCircle color="success" />;
    } else if (status === 'review' || confidence < 0.7) {
      return <ReviewIcon color="warning" />;
    } else if (status === 'processing') {
      return <ProcessingIcon color="info" />;
    } else {
      return <ErrorIcon color="error" />;
    }
  };

  const getStatusColor = (status: string): any => {
    switch (status) {
      case 'validated':
      case 'completed': return 'success';
      case 'review': return 'warning';
      case 'processing': return 'info';
      default: return 'default';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return dateString;
      }

      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffMinutes < 0) {
        // Future date (likely a timezone issue), just show the date
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
      } else if (diffMinutes === 0) {
        return 'Just now';
      } else if (diffMinutes < 60) {
        return `${Math.abs(diffMinutes)} min ago`;
      } else if (diffHours < 24) {
        return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
      } else if (diffDays === 1) {
        return 'Yesterday';
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else {
        // Basic date formatting without date-fns
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
      }
    } catch (err) {
      console.error('Date formatting error:', err, 'for date:', dateString);
      return dateString;
    }
  };

  if (loading && uploads.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ width: '100%', mb: 2 }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Your Upload History</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={() => fetchUserUploads()} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'uploaded_at'}
                  direction={orderBy === 'uploaded_at' ? orderDirection : 'asc'}
                  onClick={() => handleSort('uploaded_at')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'filename'}
                  direction={orderBy === 'filename' ? orderDirection : 'asc'}
                  onClick={() => handleSort('filename')}
                >
                  File Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Type</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? orderDirection : 'asc'}
                  onClick={() => handleSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'confidence'}
                  direction={orderBy === 'confidence' ? orderDirection : 'asc'}
                  onClick={() => handleSort('confidence')}
                >
                  Confidence
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUploads.map((upload) => (
              <TableRow
                key={`${upload.report_id}-${upload.uploaded_at}`}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => onViewReport(upload.report_id)}
              >
                <TableCell>{formatDate(upload.uploaded_at)}</TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                    {upload.filename}
                  </Typography>
                  {upload.file_size > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(upload.file_size)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip label={upload.file_type} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getStatusIcon(upload.status, upload.confidence)}
                    label={upload.status}
                    size="small"
                    color={getStatusColor(upload.status)}
                    variant={upload.user_validated ? 'filled' : 'outlined'}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">
                      {(upload.confidence * 100).toFixed(0)}%
                    </Typography>
                    {upload.confidence < 0.7 && (
                      <Tooltip title="Low confidence - manual review recommended">
                        <ReviewIcon color="warning" fontSize="small" />
                      </Tooltip>
                    )}
                    {upload.user_validated && (
                      <Tooltip title={`Validated on ${formatDate(upload.validation_timestamp)}`}>
                        <ValidatedIcon color="success" fontSize="small" />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="View Extracted Data">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewReport(upload.report_id);
                      }}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {uploads.length === 0 && !loading && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No upload history found. Start by uploading some documents above.
          </Typography>
        </Box>
      )}

      <TablePagination
        rowsPerPageOptions={[10, 20, 50]}
        component="div"
        count={hasMore ? -1 : uploads.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {loading && uploads.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading more...
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default UserHistoryTable;